import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import api from '../utils/api'
import './Auth.css'
import RequestFab from '../components/RequestFab'

const QRScan = () => {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const searchedVehicle = location.state?.vehicleNumber
  const [qrcode, setQrcode] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    vehicle_number: '',
    color: '',
    brand_or_details: '',
    seaters: '',
    mobile: '',
    email: '',
    otp: '',
    emergency_number: ''
  })
  const [formError, setFormError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // Verification states
  const [isVerified, setIsVerified] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationError, setVerificationError] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [fullVehicleNumber, setFullVehicleNumber] = useState(null) // Store full vehicle number after verification

  // Twilio states
  const [callerNumber, setCallerNumber] = useState('')
  const [messageText, setMessageText] = useState('')
  const [showCallModal, setShowCallModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [callTarget, setCallTarget] = useState('owner') // 'owner' or 'emergency'
  const [messageTarget, setMessageTarget] = useState('owner')
  const [messageType, setMessageType] = useState('sms') // 'sms' or 'whatsapp'
  const [selectedQuickMessage, setSelectedQuickMessage] = useState(null)
  const [twilioLoading, setTwilioLoading] = useState(false)
  const [twilioError, setTwilioError] = useState('')
  const [twilioSuccess, setTwilioSuccess] = useState('')
  const [twilioConfigured, setTwilioConfigured] = useState(true)
  const [requestMenuOpen, setRequestMenuOpen] = useState(false)
  const MAX_COMPOSE_CHARS = 30

  useEffect(() => {
    if (uuid) {
      fetchQRCode()
    }
  }, [uuid])

  useEffect(() => {
    if (searchedVehicle && qrcode && qrcode.has_linking && !isVerified) {
      // Auto-verify if the searched vehicle matches last 4
      const last4 = searchedVehicle.slice(-4)
      setVerificationCode(last4)
      // Small delay to ensure state is settled
      const timer = setTimeout(() => {
        handleAutoVerify(last4)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [searchedVehicle, qrcode, isVerified])

  const handleAutoVerify = async (code) => {
    setVerifying(true)
    try {
      const response = await api.post(`/qr/${uuid}/verify`, { verification_code: code })
      if (response.data.success && response.data.data?.verified) {
        setIsVerified(true)
      }
    } catch (e) {
      console.error('Auto-verify failed:', e)
    } finally {
      setVerifying(false)
    }
  }

  const fetchQRCode = async () => {
    try {
      setLoading(true)
      setError('')
      // Use public endpoint (no auth required)
      const response = await api.get(`/qr/${uuid}`)

      if (response.data.success) {
        const qr = response.data.data.qrcode
        setQrcode(qr)
        // If already has linking, never show form. Otherwise show form
        if (qr.has_linking && qr.linking) {
          setShowForm(false) // Never show form when vehicle is already linked
        } else {
          setShowForm(true) // Show form only when no vehicle is linked
        }
      }
    } catch (error) {
      console.error('Error fetching QR code:', error)

      // Check for network errors
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        setError('Unable to connect to server. Please check your internet connection and ensure the server is running.')
      } else if (error.response?.status === 404) {
        setError('QR code not found. Please check the QR code and try again.')
      } else if (error.response?.data?.message) {
        setError(error.response.data.message)
      } else {
        setError('Failed to load QR code. Please try again later.')
      }
    } finally {
      setLoading(false)
    }
  }

  if (error) {
    return (
      <div className="auth-page qr-page">
        <div className="container">
          <div className="auth-card">
            <h2>⚠️ Connection Error</h2>
            <p className="auth-subtitle">{error}</p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button className="btn-primary btn-full" onClick={() => window.location.reload()}>
                Retry
              </button>
              <button className="btn-secondary btn-full" onClick={() => navigate('/')}>
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === 'vehicle_number') {
      value = value.toUpperCase();
    }

    if (name === 'mobile' || name === 'emergency_number') {
      // Restrict to digits and max 10 characters
      value = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData({
      ...formData,
      [name]: value
    })
    setFormError('')
  }

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 4) {
      setVerificationError('Please enter 4 characters')
      return
    }

    if (!qrcode) {
      setVerificationError('QR code information not loaded. Please refresh the page.')
      return
    }

    setVerificationError('')
    setVerifying(true)

    try {
      const requestBody = {
        verification_code: verificationCode
      }

      // Include QR code ID if available
      if (qrcode && qrcode.id) {
        requestBody.qr_id = qrcode.id
      }

      console.log('Verifying with:', { uuid, verification_code: verificationCode, qr_id: qrcode?.id })

      const response = await api.post(`/qr/${uuid}/verify`, requestBody)

      if (response.data.success && response.data.data?.verified) {
        setIsVerified(true)
        setVerificationError('')
        setVerificationCode('')
        // Note: Vehicle number is still masked in API, but we can show what we have
        // The API doesn't expose full numbers for privacy
      } else {
        setVerificationError(response.data.message || 'Incorrect code. Please try again.')
        setVerificationCode('') // Clear input on error
      }
    } catch (error) {
      console.error('Verification error:', error)
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestBody: { verification_code: verificationCode, qr_id: qrcode?.id }
      })

      // Show more detailed error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Verification failed. Please try again.'

      setVerificationError(errorMessage)
      setVerificationCode('') // Clear input on error
    } finally {
      setVerifying(false)
    }
  }

  const handleResetVerification = () => {
    setIsVerified(false)
    setVerificationCode('')
    setVerificationError('')
  }

  // Quick message options - predefined vehicle report templates
  const QUICK_MESSAGES = [
    { id: 'lights', label: 'The lights of this car is on.', issue: 'has its lights on' },
    { id: 'noparking', label: 'The car is in no parking.', issue: 'is in No-Parking Area' },
    { id: 'towed', label: 'The car is getting towed.', issue: 'is getting towed' },
    { id: 'open', label: 'The window or car is open.', issue: 'has its window or car open' },
    // Keep issues <= 30 chars (API/UI constraint for composed message)
    { id: 'wrong', label: 'Something wrong with this car.', issue: 'needs your attention' }
  ]

  const formatVehicleAlertMessage = (issueSuffix) => {
    const ownerName = qrcode?.linking
      ? [qrcode.linking.first_name, qrcode.linking.last_name].filter(Boolean).join(' ').trim() || 'Vehicle Owner'
      : 'Vehicle Owner'
    const plate = qrcode?.linking?.vehicle_number || 'N/A'
    return `NAH44 Vehicle Update: Hi ${ownerName}, a user reported your vehicle with Plate Number # ${plate}, ${issueSuffix}.`
  }

  const formatWhatsAppNumber = (mobile) => {
    // Remove all non-digit characters
    let cleaned = mobile.replace(/[^0-9]/g, '')

    // If number doesn't start with country code, assume it's Indian (+91)
    // For other countries, you might need to adjust this logic
    if (cleaned.length === 10) {
      cleaned = '91' + cleaned // Add India country code
    }

    return cleaned
  }

  // Twilio handlers
  const handleTwilioCall = async () => {
    if (!callerNumber.trim()) {
      setTwilioError('Please enter your phone number')
      return
    }

    setTwilioLoading(true)
    setTwilioError('')
    setTwilioSuccess('')

    try {
      // Callback Flow: Backend calls User first, then connects to Owner
      const response = await api.post(`/twilio/call`, {
        qr_uuid: uuid,
        caller_number: callerNumber.startsWith('+') ? callerNumber : `+91${callerNumber}`,
        target: callTarget // 'owner' or 'emergency'
      })

      if (response.data.success) {
        setTwilioSuccess('Call initiated! You will receive a call from our system shortly.')

        setTimeout(() => {
          setShowCallModal(false)
          setTwilioSuccess('')
          // Do not clear callerNumber so it can be reused for WhatsApp
        }, 3000)
      }
    } catch (error) {
      console.error('Twilio call error:', error)
      setTwilioError(error.response?.data?.message || 'Failed to initiate call')
    } finally {
      setTwilioLoading(false)
    }
  }

  const handleTwilioSMS = async () => {
    if (!messageText.trim()) {
      setTwilioError('Please enter a message')
      return
    }

    if (messageText.trim().length > MAX_COMPOSE_CHARS) {
      setTwilioError(`Message must be ${MAX_COMPOSE_CHARS} characters or less`)
      return
    }

    setTwilioLoading(true)
    setTwilioError('')
    setTwilioSuccess('')

    try {
      const payload = {
        qr_uuid: uuid,
        message: messageText,
        target: messageTarget
      }

      // Send to both SMS and WhatsApp simultaneously
      const [smsResult, whatsappResult] = await Promise.allSettled([
        api.post('/twilio/sms', payload),
        api.post('/twilio/whatsapp', payload)
      ])

      const smsOk = smsResult.status === 'fulfilled' && smsResult.value?.data?.success
      const waOk = whatsappResult.status === 'fulfilled' && whatsappResult.value?.data?.success

      if (smsOk && waOk) {
        setTwilioSuccess('Message sent via SMS & WhatsApp!')
      } else if (smsOk) {
        setTwilioSuccess('SMS sent! WhatsApp delivery failed.')
      } else if (waOk) {
        setTwilioSuccess('WhatsApp sent! SMS delivery failed.')
      } else {
        const smsErr = smsResult.reason?.response?.data?.message || smsResult.reason?.message || 'SMS failed'
        const waErr = whatsappResult.reason?.response?.data?.message || whatsappResult.reason?.message || 'WhatsApp failed'
        setTwilioError(`SMS: ${smsErr} | WhatsApp: ${waErr}`)
        setTwilioLoading(false)
        return
      }

      setTimeout(() => {
        setShowMessageModal(false)
        setTwilioSuccess('')
        setMessageText('')
      }, 2000)
    } catch (error) {
      console.error('Message send error:', error)
      setTwilioError(error.message || 'Failed to send message')
    } finally {
      setTwilioLoading(false)
    }
  }

  const openCallModal = (target) => {
    setCallTarget(target)
    setShowCallModal(true)
    setTwilioError('')
    setTwilioSuccess('')
  }

  const openMessageModal = (target, type = 'sms') => {
    setMessageTarget(target)
    setMessageType(type)
    setSelectedQuickMessage(null)
    setMessageText('')
    setShowMessageModal(true)
    setTwilioError('')
    setTwilioSuccess('')
  }

  const handleQuickMessageClick = (item) => {
    setSelectedQuickMessage(item.id)
    // MSG91 Flow uses VAR3 for this; backend will build the final SMS
    setMessageText(String(item.issue || '').slice(0, MAX_COMPOSE_CHARS))
  }

  const [otpSent, setOtpSent] = useState(false)
  const [sendingOTP, setSendingOTP] = useState(false)

  const handleSendOTP = async () => {
    setFormError('')
    if (!formData.mobile.trim() || formData.mobile.length !== 10) {
      setFormError('Please enter a valid 10-digit mobile number')
      return
    }

    setSendingOTP(true)
    try {
      const response = await api.post('/auth/otp/generate', { mobile: formData.mobile })
      if (response.data.success) {
        setOtpSent(true)
        if (response.data.data?.otp && process.env.NODE_ENV === 'development') {
          setFormError(`OTP sent! (Dev - OTP: ${response.data.data.otp})`)
        } else {
          setSuccessMessage('OTP sent to your mobile!')
          setTimeout(() => setSuccessMessage(''), 3000)
        }
      } else {
        setFormError(response.data.message || 'Failed to send OTP')
      }
    } catch (error) {
      console.error('OTP Send error:', error)
      setFormError('Failed to send OTP. Please try again.')
    } finally {
      setSendingOTP(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError('')

    const firstName = (formData.first_name || '').trim()
    const lastName = (formData.last_name || '').trim()
    if (!firstName && !lastName) {
      setFormError('First name or Last name is required')
      return
    }

    if (!formData.mobile.trim()) {
      setFormError('Mobile number is required')
      return
    }

    if (!/^[0-9]{10}$/.test(formData.mobile)) {
      setFormError('Mobile number must be exactly 10 digits')
      return
    }

    if (!otpSent) {
      await handleSendOTP()
      return
    }

    if (!formData.otp || formData.otp.length !== 6) {
      setFormError('Please enter the 6-digit OTP')
      return
    }

    if (formData.emergency_number && !/^[0-9]{10}$/.test(formData.emergency_number)) {
      setFormError('Emergency number must be exactly 10 digits')
      return
    }

    setSubmitting(true)

    try {
      // Step 1: Verify OTP (backend will create user if doesn't exist)
      const fullName = [firstName, lastName].filter(Boolean).join(' ')
      const verifyRes = await api.post('/auth/otp/verify', {
        mobile: formData.mobile,
        otp: formData.otp,
        name: fullName
      })

      if (!verifyRes.data.success) {
        setFormError(verifyRes.data.message || 'OTP verification failed')
        setSubmitting(false)
        return
      }

      const linkData = {
        ...formData,
        name: fullName,
        first_name: firstName,
        last_name: lastName
      }
      const response = await api.post(`/qr/${uuid}/link`, linkData)
      if (response.data.success) {
        setSuccessMessage('Vehicle details saved and account created successfully!')
        setFormError('')
        // Refresh QR code data
        setTimeout(() => {
          fetchQRCode()
          setShowForm(false)
          setSuccessMessage('')
        }, 1500)
      }
    } catch (error) {
      console.error('Error linking vehicle:', error)
      setFormError(error.response?.data?.message || 'Error saving vehicle details')
      setSuccessMessage('')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading QR code...</p>
      </div>
    )
  }



  return (
    <div className="qr-page">
      <div style={{ position: 'fixed', top: '1.5rem', left: '1.5rem', zIndex: 100 }}>
        <button onClick={() => navigate('/')} className="btn-xs btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(15, 23, 42, 0.6)' }}>
          🏠 Home
        </button>
      </div>
      <div className="qr-container">

        {/* Loading State handled above, but if we have qrcode data: */}
        {qrcode && (
          <div className="qr-card">
            <div className="qr-header">
              <span className="badge badge-info mb-3">Verified Vehicle Tag</span>
              <h2 className="qr-title">Vehicle Found</h2>
              <div className="qr-subtitle">Code: {qrcode.code_uuid}</div>
            </div>

            {qrcode.has_linking && qrcode.linking ? (
              <>
                {!isVerified ? (
                  /* Verification Step */
                  <div className="animate-fadeIn">
                    <div className="status-card status-info">
                      <div className="status-title">🚗 Vehicle Details</div>
                      <div className="detail-row">
                        <span className="detail-label">Brand/Model</span>
                        <span className="detail-value">{qrcode.linking.brand_or_details || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Color</span>
                        <span className="detail-value">{qrcode.linking.color || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Vehicle No.</span>
                        <span className="detail-value">
                          {qrcode.linking.vehicle_number
                            ? (qrcode.linking.vehicle_number.length >= 4
                              ? qrcode.linking.vehicle_number.slice(0, -4) + '****'
                              : '****')
                            : 'Hidden'}
                        </span>
                      </div>
                    </div>

                    <div className="action-section">
                      <div className="action-title">🔐 Security Check</div>
                      <p className="text-secondary text-center mb-4 text-sm">
                        Enter the last 4 digits of the vehicle number to unlock contact details.
                      </p>

                      {verificationError && (
                        <div className="alert alert-error mb-4">
                          ⚠️ {verificationError}
                        </div>
                      )}

                      <div className="verify-input-group">
                        <input
                          type="text"
                          className="verify-input"
                          value={verificationCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase()
                            setVerificationCode(value)
                            setVerificationError('')
                          }}
                          placeholder="— — — —"
                          maxLength={4}
                          onKeyPress={(e) => e.key === 'Enter' && verificationCode.length === 4 && handleVerify()}
                        />
                      </div>

                      <button
                        className="btn btn-primary w-full"
                        onClick={handleVerify}
                        disabled={verificationCode.length !== 4 || verifying}
                      >
                        {verifying ? 'Verifying...' : 'Verify Identity'}
                      </button>
                    </div>

                    <p className="text-xs text-center text-secondary opacity-70">
                      Why verify? To protect vehicle owners from spam, we require physical proximity verification.
                    </p>
                  </div>
                ) : (
                  /* Verified - Show Contact Options */
                  <div className="animate-fadeIn">
                    <div className="status-card status-verified">
                      <div className="status-title text-success">
                        <span>✓ Verified Access</span>
                        <button onClick={handleResetVerification} className="btn-xs btn-outline ml-auto">Reset</button>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Vehicle No.</span>
                        <span className="detail-value">{qrcode.linking.vehicle_number || 'N/A'}</span>
                      </div>
                    </div>

                    {/* Phone Section */}
                    <div className="action-section">
                      <div className="action-title">📞 Phone</div>
                      <div className="action-grid">
                        {(qrcode.linking.mobile_no || qrcode.linking.mobile) && (
                          <button
                            className="btn btn-primary w-full pulse-animation"
                            onClick={() => openCallModal('owner')}
                          >
                            <span className="icon">📞</span> Normal Phone
                          </button>
                        )}

                        {qrcode.linking.emergency_number && (
                          <button
                            className="btn btn-emergency w-full"
                            onClick={() => openCallModal('emergency')}
                          >
                            <span className="icon">🆘</span> Emergency
                          </button>
                        )}
                        {!(qrcode.linking.mobile_no || qrcode.linking.mobile) && !qrcode.linking.emergency_number && (
                          <p className="text-sm text-secondary text-center w-full">No phone numbers available</p>
                        )}
                      </div>
                      <p className="text-xs text-center mt-4 text-secondary" style={{ opacity: 0.7, lineHeight: '1.5' }}>
                        Calls are bridged securely. Your number is never revealed.
                      </p>
                    </div>

                    {/* Message Section - Single flow: click Message → pick channel → pick quick message */}
                    <div className="action-section">
                      <div className="action-title">💬 Message</div>
                      <div className="action-grid">
                        {(qrcode.linking.mobile_no || qrcode.linking.mobile) ? (
                          <button
                            className="btn btn-primary w-full"
                            onClick={() => openMessageModal('owner', 'sms')}
                          >
                            <span className="icon">💬</span> Send Message
                          </button>
                        ) : (
                          <p className="text-sm text-secondary text-center w-full" style={{ padding: '1rem', opacity: 0.6 }}>
                            No contact number available for messaging
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-center mt-4 text-secondary" style={{ opacity: 0.7, lineHeight: '1.5' }}>
                        Pick a quick message. Sent securely via SMS & WhatsApp.
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* No Linking - Registration Form */
              <div className="animate-fadeIn">
                <div className="status-card status-warning text-center">
                  <div className="status-title justify-center text-warning">⚠️ Unregistered Tag</div>
                  <p className="text-sm text-secondary">
                    This QR tag is active but not linked to a vehicle yet.
                  </p>
                </div>

                {!showForm ? (
                  <button className="btn btn-primary w-full" onClick={() => setShowForm(true)}>
                    Link Vehicle Now
                  </button>
                ) : (
                  <div className="action-section">
                    <h3 className="text-center text-white mb-4">Register Vehicle</h3>
                    <form onSubmit={handleSubmit}>
                      {successMessage && <div className="alert alert-success">{successMessage}</div>}
                      {formError && <div className="alert alert-error">{formError}</div>}

                      <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div className="form-group mb-3">
                          <label className="text-sm text-secondary mb-1 block">First Name *</label>
                          <input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            className="form-input w-full p-2 rounded"
                            placeholder="First Name"
                            disabled={otpSent}
                          />
                        </div>
                        <div className="form-group mb-3">
                          <label className="text-sm text-secondary mb-1 block">Last Name *</label>
                          <input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            className="form-input w-full p-2 rounded"
                            placeholder="Last Name"
                            disabled={otpSent}
                          />
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label className="text-sm text-secondary mb-1 block">Vehicle Number</label>
                        <input
                          type="text"
                          name="vehicle_number"
                          value={formData.vehicle_number}
                          onChange={handleChange}
                          className="form-input w-full p-2 rounded"
                          placeholder="XX00YY0000"
                          required
                          disabled={otpSent}
                        />
                      </div>

                      <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div className="form-group mb-3">
                          <label className="text-sm text-secondary mb-1 block">Color</label>
                          <input
                            type="text"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                            className="form-input w-full p-2 rounded"
                            placeholder="e.g. Red"
                            disabled={otpSent}
                          />
                        </div>
                        <div className="form-group mb-3">
                          <label className="text-sm text-secondary mb-1 block">Model</label>
                          <input
                            type="text"
                            name="brand_or_details"
                            value={formData.brand_or_details}
                            onChange={handleChange}
                            className="form-input w-full p-2 rounded"
                            placeholder="e.g. Honda City"
                            disabled={otpSent}
                          />
                        </div>
                        <div className="form-group mb-3">
                          <label className="text-sm text-secondary mb-1 block">Seaters</label>
                          <input
                            type="text"
                            name="seaters"
                            value={formData.seaters}
                            onChange={handleChange}
                            className="form-input w-full p-2 rounded"
                            placeholder="e.g. 5"
                            disabled={otpSent}
                          />
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <label className="text-sm text-secondary mb-1 block">Mail (Email)</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="form-input w-full p-2 rounded"
                          placeholder="your@email.com"
                          disabled={otpSent}
                        />
                      </div>

                      <div className="form-group mb-3">
                        <label className="text-sm text-secondary mb-1 block">Your Mobile (Owner) *</label>
                        <div className="mobile-input-wrapper">
                          <span className="country-code">+91</span>
                          <input
                            type="tel"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            className="form-input flex-1"
                            required
                            placeholder="10-digit number"
                            maxLength={10}
                            disabled={otpSent}
                          />
                        </div>
                      </div>

                      {otpSent && (
                        <div className="form-group mb-3 animate-slideUp">
                          <label className="text-sm text-warning mb-1 block">Enter 6-Digit OTP</label>
                          <input
                            type="text"
                            name="otp"
                            value={formData.otp}
                            onChange={(e) => {
                               const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                               setFormData({...formData, otp: val});
                            }}
                            className="form-input w-full p-2 rounded border-warning"
                            placeholder="000000"
                            maxLength={6}
                            required
                          />
                          <div className="text-right mt-1">
                            <button type="button" onClick={handleSendOTP} className="text-xs text-primary underline">Resend OTP</button>
                          </div>
                        </div>
                      )}

                      <div className="form-group mb-4">
                        <label className="text-sm text-secondary mb-1 block">Emergency Contact (Optional)</label>
                        <div className="mobile-input-wrapper">
                          <span className="country-code">+91</span>
                          <input
                            type="tel"
                            name="emergency_number"
                            value={formData.emergency_number}
                            onChange={handleChange}
                            className="form-input flex-1"
                            placeholder="Family/Friend number"
                            maxLength={10}
                            disabled={otpSent}
                          />
                        </div>
                      </div>

                      <button type="submit" className="btn btn-primary w-full mt-2" disabled={submitting || sendingOTP}>
                        {submitting ? 'Processing...' : (otpSent ? 'Verify & Link' : 'Send OTP')}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            )}

            <div className="text-center mt-6">
              <a href="/" className="login-link text-sm">HOME &rarr;</a>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {(showCallModal || showMessageModal) && (
        <div className="modal-overlay" onClick={() => { setShowCallModal(false); setShowMessageModal(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
              {showCallModal
                ? `Secure Call - ${callTarget === 'emergency' ? 'Emergency' : 'Owner'}`
                : `Secure Message (${messageTarget === 'emergency' ? 'Emergency' : 'Owner'})`}
            </h3>
            <p className="text-secondary mb-5 text-sm" style={{ opacity: 0.85 }}>
              {showCallModal
                ? "Enter your number to receive a bridged call. Your privacy is protected."
                : "Send a message without revealing your number."}
            </p>

            {showCallModal && (
              <div className="mobile-input-wrapper mb-4" style={{ background: 'var(--bg-input)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <span className="country-code">+91</span>
                <input
                  type="tel"
                  className="form-input flex-1 p-3 rounded text-lg"
                  style={{ background: 'transparent', border: 'none', boxShadow: 'none', color: 'var(--text-primary)' }}
                  placeholder="Your 10-digit Number"
                  value={callerNumber}
                  maxLength={10}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setCallerNumber(val);
                  }}
                />
              </div>
            )}

            {showMessageModal && (
              <div className="msg-modal-body">
                <div className="msg-modal-section">
                  <label className="msg-modal-label">Pick a message</label>
                  <div className="msg-quick-list">
                    {QUICK_MESSAGES.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`msg-quick-item ${selectedQuickMessage === item.id ? 'selected' : ''}`}
                        onClick={() => handleQuickMessageClick(item)}
                      >
                        <span className="msg-quick-icon">
                          {item.id === 'lights' && '💡'}
                          {item.id === 'noparking' && '🚫'}
                          {item.id === 'towed' && '🚛'}
                          {item.id === 'open' && '🪟'}
                          {item.id === 'wrong' && '🔔'}
                        </span>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="msg-modal-section" style={{ marginTop: '1rem' }}>
                  <label className="msg-modal-label">
                    Or compose (max {MAX_COMPOSE_CHARS} chars)
                  </label>
                  <div
                    style={{
                      background: 'var(--bg-input)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: '12px',
                      padding: '0.75rem'
                    }}
                  >
                    <input
                      type="text"
                      value={messageText}
                      maxLength={MAX_COMPOSE_CHARS}
                      placeholder="Type your message…"
                      className="form-input w-full"
                      style={{
                        width: '100%',
                        background: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        color: 'var(--text-primary)',
                        padding: 0
                      }}
                      onChange={(e) => {
                        const v = (e.target.value || '').slice(0, MAX_COMPOSE_CHARS)
                        setMessageText(v)
                        if (selectedQuickMessage) setSelectedQuickMessage(null)
                        if (twilioError) setTwilioError('')
                      }}
                    />
                    <div
                      className="text-xs text-secondary"
                      style={{
                        marginTop: '0.5rem',
                        opacity: 0.75,
                        display: 'flex',
                        justifyContent: 'flex-end'
                      }}
                    >
                      {Math.max(0, MAX_COMPOSE_CHARS - (messageText?.length || 0))} left
                    </div>
                  </div>
                </div>
              </div>
            )}

            {twilioError && <div className="text-red-400 text-sm mb-3 bg-red-900/20 p-2 rounded-lg">⚠️ {twilioError}</div>}
            {twilioSuccess && <div className="text-green-400 text-sm mb-3 bg-green-900/20 p-2 rounded-lg">✓ {twilioSuccess}</div>}

            <div className={`flex gap-3 ${showMessageModal ? 'msg-modal-actions' : ''}`}>
              <button
                type="button"
                className="btn btn-outline flex-1 py-2.5 rounded-xl font-semibold"
                onClick={() => { setShowCallModal(false); setShowMessageModal(false); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary flex-1 py-2.5 rounded-xl font-semibold"
                onClick={showCallModal ? handleTwilioCall : handleTwilioSMS}
                disabled={twilioLoading}
              >
                {twilioLoading ? 'Processing...' : (showCallModal ? 'Call Now' : 'Send')}
              </button>
            </div>
          </div>
        </div>
      )}
      <RequestFab
        isOpen={requestMenuOpen}
        onClick={() => setRequestMenuOpen(!requestMenuOpen)}
      />
    </div>
  )
}

export default QRScan

